import { INTEGRATION_PROVIDERS } from '@vy/types';
import { QuickBooksConfigurationFormValues } from '../QuickBooksConfigurationFormSchema';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import { raise, raiseUnsupported, validateNotNullOrUndefined } from '@vy/utils';
import {
  INTEGRATION_TYPES,
  INVOICE_NUMBER_SYNC_STRATEGY,
  INVOICE_PAYMENT_STATUS_SYNC_STRATEGY,
  TAX_EXPORT_STRATEGY,
  QuickBooksIntegrationSettings,
  QuickBooksIntegrationSettingsSchema,
} from '@vy/integrations-types';
import { SYNC_DIRECTIONS } from '../../../stripe-components/configure-stripe-integration/StripeConfigurationFormSchema';
import { useMemo } from 'react';

type FetchQuickBooksSettingsResult = {
  integrationId: string;
  configuration: QuickBooksConfigurationFormValues;
};

export function useFetchQuickBooksSettings(): FetchQuickBooksSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_PROVIDERS.quickBooks);
  const quickBooksIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(quickBooksIntegrationSettings, 'quickBooksIntegrationSettings');

  const parsedQuickBooksIntegrationSettings = QuickBooksIntegrationSettingsSchema.parse(
    quickBooksIntegrationSettings.integrationSettings,
  );

  const mappedSettings = mapIntegrationSettingsToFormValues(parsedQuickBooksIntegrationSettings);

  return useMemo(
    () => ({
      integrationId: quickBooksIntegrationSettings.integrationId,
      configuration: mappedSettings,
    }),
    [quickBooksIntegrationSettings.integrationId, mappedSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: QuickBooksIntegrationSettings,
): QuickBooksConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_TYPES.QUICKBOOKS) {
    raiseUnsupported(integrationSettings.integrationProvider);
  }

  const config = integrationSettings.integrationConfiguration;

  if (!config) {
    raise('Integration configuration is missing');
  }

  const authConfig = config.authConfig;
  const autoExportInvoices = !!config.generalConfig?.autoExportInvoices;
  const autoImportCustomers = config.generalConfig?.autoImportCustomers ?? false;
  const invoiceNumberSyncStrategy =
    config.generalConfig?.invoiceNumberSyncStrategy ?? INVOICE_NUMBER_SYNC_STRATEGY.KEEP_SEPARATE;
  const invoicePaymentStatusSyncStrategy =
    config.generalConfig?.invoicePaymentStatusSyncStrategy ??
    INVOICE_PAYMENT_STATUS_SYNC_STRATEGY.KEEP_SEPARATE;
  const exportBankDetails = config.generalConfig?.exportBankDetails ?? false;
  const defaultStripeDepositToAccountId = config.generalConfig?.defaultStripeDepositToAccountId ?? null;
  const defaultDepositToAccountId = config.generalConfig?.defaultDepositToAccountId ?? null;

  const syncCustomerDirection = autoImportCustomers
    ? SYNC_DIRECTIONS.SYNC_TO_VAYU
    : SYNC_DIRECTIONS.AUTO_SYNC_DISABLED;

  validateNotNullOrUndefined(authConfig, 'authConfig');

  const taxExportStrategy =
    config.generalConfig?.taxExportStrategy ?? TAX_EXPORT_STRATEGY.INCLUDE_IN_AMOUNT;
  const accountTaxItem = config.generalConfig?.accountTaxItem;

  return {
    syncCustomer: syncCustomerDirection,
    syncApprovedInvoices: autoExportInvoices,
    autoExportCreditMemos: config.generalConfig?.autoExportCreditMemos ?? false,
    invoiceNumberSyncStrategy,
    invoicePaymentStatusSyncStrategy,
    exportBankDetails,
    defaultStripeDepositToAccountId,
    defaultDepositToAccountId,
    taxExportStrategy,
    accountTaxItem,
  };
}
